import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Paper } from '@mui/material';
import { ErrorOutline, Refresh, Home } from '@mui/icons-material';
import { GluestackContainer } from './GluestackContainer';
import { Text } from '@gluestack-ui/themed';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Enhanced Error Boundary Component
 * 
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI with recovery options.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });

    // Error reporting: no external service (Sentry/LogRocket) is wired up
    // in this project, so `onError` is expected to be passed by the caller
    // to route into the app's own error-reporting mechanism
    // (see App.tsx, which forwards to useErrorHandler's handleError).
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <GluestackContainer maxWidth="md">
          <Box
            sx={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
            }}
          >
            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, sm: 4, md: 5 },
                borderRadius: 2,
                textAlign: 'center',
                maxWidth: 600,
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #f56565 0%, #c53030 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 20px rgba(245, 101, 101, 0.3)',
                  }}
                >
                  <ErrorOutline sx={{ fontSize: 48, color: 'white' }} />
                </Box>
              </Box>

              <Text
                as="h1"
                variant="h4"
                weight="bold"
                className="mb-2 text-foreground"
              >
                Oops! Something went wrong
              </Text>

              <Text variant="body1" className="mb-4 text-foreground/80">
                We're sorry for the inconvenience. The application encountered an unexpected error.
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <>
                    <br />
                    <br />
                    <strong>Error:</strong> {this.state.error.message}
                  </>
                )}
              </Text>

              {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
                <Box
                  sx={{
                    mt: 3,
                    mb: 3,
                    p: 2,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    textAlign: 'left',
                    overflow: 'auto',
                    maxHeight: 200,
                  }}
                >
                  <Text
                    as="pre"
                    variant="caption"
                    className="font-mono text-[0.75rem] whitespace-pre-wrap break-words"
                  >
                    {this.state.errorInfo.componentStack}
                  </Text>
                </Box>
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  justifyContent: 'center',
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Refresh />}
                  onClick={this.handleReset}
                  sx={{
                    px: 4,
                    py: 1.5,
                    background: 'linear-gradient(135deg, #009A56 0%, #30C273 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #008457 0%, #009A56 100%)',
                    },
                  }}
                >
                  Try Again
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<Home />}
                  onClick={this.handleGoHome}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderColor: '#009A56',
                    color: '#009A56',
                    '&:hover': {
                      borderColor: '#008457',
                      backgroundColor: 'rgba(0, 154, 86, 0.04)',
                    },
                  }}
                >
                  Go Home
                </Button>

                {process.env.NODE_ENV === 'development' && (
                  <Button
                    variant="text"
                    onClick={this.handleReload}
                    sx={{
                      px: 4,
                      py: 1.5,
                      color: 'text.secondary',
                    }}
                  >
                    Reload Page
                  </Button>
                )}
              </Box>
            </Paper>
          </Box>
        </GluestackContainer>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook version for functional components
 */
export const useErrorHandler = (error?: Error | null) => {
  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);
};
